import { format } from 'date-fns';
import type { TrafficRow } from './rows';

export interface MirrorSeries {
  dataKey: keyof TrafficRow;
  label: string;
  color: string;
  format: (value: number) => string;
}

/**
 * Value first, series name second — the reader already knows which chart they
 * are pointing at.
 *
 * Magnitudes throughout: the sign is the direction channel, and printing "-412"
 * would make the reader do the translation the geometry already did for them.
 */
export function MirrorTooltip({
  active,
  payload,
  label,
  series,
  spansDays,
}: {
  active?: boolean;
  payload?: { dataKey?: string | number; value?: number | null }[];
  label?: number;
  series: MirrorSeries[];
  spansDays: boolean;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const rows = payload.flatMap((entry) => {
    const spec = series.find((s) => s.dataKey === entry.dataKey);
    // A gap has no value to report, so its row is omitted rather than shown as
    // a zero the store never claimed.
    if (!spec || entry.value === undefined || entry.value === null) return [];
    return [{ spec, value: Math.abs(entry.value) }];
  });

  if (rows.length === 0) return null;

  return (
    <div className="rounded-md border border-border bg-popover text-popover-foreground px-3 py-2 shadow-md">
      <p className="text-[11px] text-muted-foreground mb-1.5">
        {typeof label === 'number' ? format(new Date(label), spansDays ? 'MMM d, HH:mm' : 'HH:mm:ss') : ''}
      </p>
      <ul className="space-y-1">
        {rows.map(({ spec, value }) => (
          <li key={spec.dataKey} className="flex items-baseline gap-2 text-xs">
            <span
              className="inline-block w-3 h-0.5 rounded-full shrink-0"
              style={{ backgroundColor: spec.color }}
              aria-hidden="true"
            />
            <span className="font-semibold tabular-nums">{spec.format(value)}</span>
            <span className="text-muted-foreground">{spec.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
